import { useFragment } from "@/generated";
import {
  SetSlideDomainsDocument,
  type SlideDomainEnum,
  SlideFieldsFragmentDoc,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";
import { reconcileFilteredSlideList } from "./reconcileFilteredSlideLists";

/**
 * Replaces the report dimensions of one or more slides. Takes a list of IDs so the
 * single-slide editor and bulk selection share one path; the backend applies the whole
 * batch in one transaction, so all slides change or none do.
 */
export function useSetSlideDomains() {
  const [setSlideDomainsMutation, { loading }] = useMutation(SetSlideDomainsDocument, {
    update(cache, { data }, { variables }) {
      if (!data?.setSlideDomains) return;

      // GraphQL coerces a lone value into a list, so the variable can arrive either way.
      const newDomains = [variables?.domains ?? []].flat();
      if (!newDomains.length) return;

      // Returned slides include synced linked copies, so views in other lessons reconcile too.
      const changedIds = new Set(
        data.setSlideDomains.map((slide) => useFragment(SlideFieldsFragmentDoc, slide).id)
      );

      cache.modify({
        fields: {
          slidesByLessonId(existingData, { readField, storeFieldName, DELETE }) {
            if (!existingData?.items) return existingData;
            const decision = reconcileFilteredSlideList(
              storeFieldName,
              "report",
              newDomains.map(String),
              changedIds,
              existingData.items,
              readField
            );
            if (decision.action === "evict") return DELETE;
            if (decision.action === "replace") {
              return { ...existingData, items: decision.items, total: decision.items.length };
            }
            return existingData;
          },
        },
      });
    },
  });

  const setSlideDomains = async (slideIds: string[], domains: SlideDomainEnum[]) => {
    return withToast(
      async () => {
        const result = await setSlideDomainsMutation({
          variables: { slideIds, domains },
        });

        const maskedSlides = result.data?.setSlideDomains ?? [];
        return maskedSlides.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));
      },
      {
        loading: "Updating slide domains...",
        success: `Successfully updated ${slideIds.length} slide${slideIds.length === 1 ? "" : "s"}`,
        error: "Failed to update slide domains. Please try again.",
      },
      "setSlideDomains"
    );
  };

  return {
    setSlideDomains,
    loading,
  };
}
